import React, { useEffect, useState } from 'react';
import { InputGroup, TextField } from '../../../../../../Styles';
import { formatCurrency, stripCurrency } from '../../../../../../../commons/utils';
import * as MUI from '@mui/material';
import styled from 'styled-components';
import { Theme } from '../../../../../../Styles';

const BucketCard = styled.div`
	border: 1px solid #EAEAEA;
	border-radius: 8px;
	padding: 1rem;
	margin: 0.5rem 0;
	background-color: #FAFAFA;
`;

const BucketHeader = styled.div`
	font-size: 14px;
	font-weight: 600;
	margin-bottom: 1rem;
	color: ${Theme.black};
`;

const FieldsRow = styled.div`
	display: flex;
	flex-direction: row;
	gap: 1rem;
	@media (max-width: 768px) {
		flex-direction: column;
	}
`;

const BucketPriceRebate = ({ 
	bucketId, 
	bucketName, 
	price, 
	rebate, 
	priceError, 
	rebateError, 
	onPriceChange, 
	onRebateChange, 
	onPriceError, 
	onRebateError,
	rebateEnabled = true,
	disabled = false,
	isAlloy = false
}) => {
	const [priceValue, setPriceValue] = useState(price || '');
	const [rebateValue, setRebateValue] = useState(rebate || '');
	const hasValidPrice = () => {
		if (!priceValue || priceValue === '' || priceValue === null || priceValue === undefined) {
			return false;
		}
		const numValue = parseFloat(stripCurrency(priceValue));
		return !isNaN(numValue) && numValue > 0;
	};

	useEffect(() => {
		setPriceValue(price || '');
	}, [price]);

	useEffect(() => {
		setRebateValue(rebate || '');
	}, [rebate]);

	useEffect(() => {
		const priceIsValid = priceValue && priceValue !== '' && priceValue !== null && priceValue !== undefined;
		let numValue = null;
		if (priceIsValid) {
			numValue = parseFloat(stripCurrency(priceValue));
		}
		const isValid = priceIsValid && !isNaN(numValue) && numValue > 0;
		
		if (!isValid && rebateValue && rebateValue !== '') {
			setRebateValue('');
			onRebateChange('');
			onRebateError(null);
		}
	}, [priceValue, rebateValue, onRebateChange, onRebateError]);

	const validatePrice = (value) => {
		if (!value || value === '' || value === null || value === undefined) {
			onPriceError(null);
			if (rebateValue && rebateValue !== '') {
				setRebateValue('');
				onRebateChange('');
				onRebateError(null);
			}
			return true;
		}
		const numValue = parseFloat(stripCurrency(value));
		if (isNaN(numValue) || numValue <= 0) {
			onPriceError('Enter a valid price greater than 0.');
			return false;
		}
		onPriceError(null);
		return true;
	};

	const validateRebate = (value) => {
		if (!value || value === '' || value === null || value === undefined) {
			onRebateError(null);
			return true; // Optional field
		}
		const numValue = parseFloat(value);
		if (isNaN(numValue)) {
			onRebateError('Enter a valid rebate percentage between 0 and 100.');
			return false;
		}
		if (numValue < 0 || numValue > 100) {
			onRebateError('Enter a valid rebate percentage between 0 and 100.');
			return false;
		}
		// Check decimal places
		const decimalPlaces = (value.toString().split('.')[1] || []).length;
		if (decimalPlaces > 2) {
			onRebateError('Enter a valid rebate percentage between 0 and 100.');
			return false;
		}
		onRebateError(null);
		return true;
	};

	const handlePriceChange = (e) => {
		const formatted = formatCurrency(e.target.value);
		setPriceValue(formatted);
	};

	const handlePriceBlur = (e) => {
		const value = stripCurrency(formatCurrency(e.target.value, true));
		setPriceValue(formatCurrency(value));
		if (value && value !== '') {
			validatePrice(value);
		} else {
			onPriceError(null);
			if (rebateValue && rebateValue !== '') {
				setRebateValue('');
				onRebateChange('');
				onRebateError(null);
			}
		}
		onPriceChange(value);
	};

	const handleRebateChange = (e) => {
		let value = e.target.value;
		// Allow only numbers and one decimal point
		value = value.replace(/[^0-9.]/g, '');
		// Ensure only one decimal point
		const parts = value.split('.');
		if (parts.length > 2) {
			value = parts[0] + '.' + parts.slice(1).join('');
		}
		// Limit to 2 decimal places
		if (parts[1] && parts[1].length > 2) {
			value = parts[0] + '.' + parts[1].substring(0, 2);
		}
		setRebateValue(value);
		// Only validate if there's a value (optional field)
		if (value && value !== '') {
			validateRebate(value);
		} else {
			onRebateError(null);
		}
		onRebateChange(value);
	};

	const handleRebateBlur = (e) => {
		const value = e.target.value;
		validateRebate(value);
		onRebateChange(value);
	};

	return (
		<BucketCard>
			<BucketHeader>{bucketName}</BucketHeader>
			<FieldsRow>
				<InputGroup
					label={isAlloy ? "SKU Price (per gram)" : "SKU Price"}
					markRequired={false}
					error={priceError}
					style={{ flex: 1 }}
				>
					<TextField
						name={`price_${bucketId}`}
						placeholder="$0.000"
						value={formatCurrency(priceValue)}
						disabled={disabled}
						onBlur={handlePriceBlur}
						onChange={handlePriceChange}
						error={priceError}
					/>
				</InputGroup>
				{!disabled && rebateEnabled && <InputGroup
					label="Rebate %"
					markRequired={false}
					error={rebateError}
					style={{ flex: 1 }}
				>
					<TextField
						name={`rebate_${bucketId}`}
						placeholder="0.000"
						value={rebateValue}
						disabled={disabled || !rebateEnabled || !hasValidPrice()}
						onBlur={handleRebateBlur}
						onChange={handleRebateChange}
						error={rebateError}
						endAdornment={
							<MUI.InputAdornment position="end" style={{ paddingRight: '0.5rem' }}>
								%
							</MUI.InputAdornment>
						}
					/>
				</InputGroup>}
			</FieldsRow>
		</BucketCard>
	);
};

export default BucketPriceRebate;

